import { ReactNode } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  AlertCircle,
  ArrowRight,
  Building,
  Calendar,
  Clock,
  FileText,
  Phone,
  Trash2,
  User
} from "lucide-react";
import { cn } from '@/lib/utils';
import { Tarefa } from '@/types/tarefas';
import { formatDate, formatPhone, getEtapaNome } from '@/utils/tarefas';

interface TarefaListItemProps {
  tarefa: Tarefa;
  onClick: (tarefa: Tarefa) => void;
  onStatusChange: (tarefaId: string, status: Tarefa['status']) => void;
  onVerLead?: (leadId: string) => void;
  onDelete?: (tarefaId: string) => void;
  statusBadge: ReactNode;
  prioridadeBadge: ReactNode;
  isParceiro?: boolean;
}

export function TarefaListItem({
  tarefa,
  onClick,
  onStatusChange,
  onVerLead,
  onDelete,
  statusBadge,
  prioridadeBadge,
  isParceiro = false
}: TarefaListItemProps) {
  const handleRowClick = (event: React.MouseEvent) => {
    // Parceiros não podem visualizar detalhes
    if (isParceiro) {
      return;
    }
    // Não abrir detalhes se clicar no select ou botão
    if ((event.target as HTMLElement).closest('[data-no-click]')) {
      return;
    }
    onClick(tarefa);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onDelete) {
      onDelete(tarefa.id);
    }
  };

  const isConcluded = tarefa.status === 'concluida' || tarefa.status === 'concluida_com_pendencias';

  // Verificar se a tarefa está vencida (comparando data/hora atual com data_vencimento)
  const isVencida = (): boolean => {
    if (!tarefa.data_vencimento || isConcluded) return false;
    const dataVencimento = new Date(tarefa.data_vencimento);
    const agora = new Date();
    return agora > dataVencimento;
  };

  const tarefaVencida = isVencida();

  return (
    <TableRow
      className={cn(
        "transition-colors",
        !isParceiro && "cursor-pointer hover:bg-muted/50",
        tarefa.esta_atrasada ? "bg-red-50/30" : "",
        tarefa.prioridade === 'urgente' ? "bg-orange-50/30" : ""
      )}
      onClick={handleRowClick}
    >
      <TableCell className="font-medium">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="truncate max-w-[200px]">{tarefa.titulo}</span>
            {statusBadge}
          </div>
          {tarefa.descricao && (
            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{tarefa.descricao}</p>
          )}
        </div>
      </TableCell>

      <TableCell>
        <Badge variant="outline" className="text-xs">
          {getEtapaNome(tarefa.etapa_criacao)}
        </Badge>
      </TableCell>

      <TableCell>
        {prioridadeBadge}
      </TableCell>

      <TableCell className="text-sm">
        {isConcluded && tarefa.data_conclusao ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-green-600" />
              <span className="text-green-700 font-medium">Concluída: {formatDate(tarefa.data_conclusao)}</span>
            </div>
          </div>
        ) : tarefa.data_vencimento ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{formatDate(tarefa.data_vencimento)}</span>
            </div>
            {tarefaVencida && (
              <span className="text-xs font-semibold text-red-600">
                Vencido
              </span>
            )}
            {!tarefaVencida && typeof tarefa.dias_restantes === 'number' && (
              <span className={cn(
                "text-xs font-semibold",
                tarefa.dias_restantes <= 1
                  ? "text-orange-600"
                  : tarefa.dias_restantes <= 3
                  ? "text-yellow-600"
                  : "text-muted-foreground"
              )}>
                {tarefa.dias_restantes === 0
                  ? 'Vence hoje'
                  : tarefa.dias_restantes === 1
                  ? 'Vence amanhã'
                  : `${tarefa.dias_restantes} dias restantes`}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">Sem prazo</span>
        )}
      </TableCell>

      <TableCell>
        {tarefa.lead ? (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              {tarefa.lead.tipo === 'juridica' ? (
                <Building className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <User className="h-3.5 w-3.5 text-blue-600" />
              )}
              <span className="text-sm truncate max-w-[150px]">{tarefa.lead.nome}</span>
            </div>
            {tarefa.lead.telefone && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                <span>{formatPhone(tarefa.lead.telefone)}</span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Nenhum lead</span>
        )}
      </TableCell>

      <TableCell>
        {tarefa.vendedor ? (
          <div className="flex items-center gap-1">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-sm">{tarefa.vendedor.nome}</span>
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Não atribuído</span>
        )}
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-2" data-no-click>
          {onDelete && !isParceiro && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
              title="Deletar tarefa"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {!isConcluded && !isParceiro && (
            <div className="w-32">
              <Select
                value={tarefa.status}
                onValueChange={(value) => onStatusChange(tarefa.id, value as Tarefa['status'])}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="concluida">Concluída</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
